import { Box, Image, useBoolean } from '@chakra-ui/react';
import { E2_TEMPLATE_CATEGORY } from 'hooks/usePaginatedTemplatesSearch/usePaginatedTemplatesSearch.config';
import { TemplateHit, TemplateType } from 'hooks/usePaginatedTemplatesSearch/usePaginatedTemplatesSearch.types';
import { PermissionBadge, UserType, hasTemplatePermission } from 'modules/User';
import { CSSProperties, FocusEvent, memo } from 'react';
import { areEqual } from 'react-window';

import { COLUMN_NUM, INFINITE_SCROLL_INDEX_OFFSET, PADDING_SIZE, TEMPLATE_THUMBNAIL_URL } from './TemplatesMenu.config';
import { blurTemplateOverlayButtonIfActive } from './TemplatesMenu.helpers';
import { AccessibleTemplateTag } from './common/AccessibleTemplateTag';
import { ThumbnailOverlay } from './common/ThumbnailOverlay';

interface GridInterface {
  templates: TemplateHit[];
  onSelectTemplate: (templateData: TemplateHit) => void;
  userType: UserType;
  addPageIsDisabled: boolean;
  loadingTemplateId: string | null;
  onLoadMoreAnchor: (instance: Element | null) => void;
}

interface TemplateThumbnailProps {
  data: GridInterface;
  rowIndex: number;
  columnIndex: number;
  style?: CSSProperties;
}

/**
 * TEMPLATE THUMBNAIL COMPONENT
 *
 * Renders a template thumbnail - images are pulled from image_cdn.
 * Includes hover state - if user has correct permissions, can add page from template
 * Otherwise, will show Upgrade button
 */
export const TemplateThumbnail = memo(({ data, rowIndex, columnIndex, style }: TemplateThumbnailProps) => {
  const [isHovered, setIsHovered] = useBoolean(false);
  const index = rowIndex * COLUMN_NUM + columnIndex;
  const { templates, onSelectTemplate, userType, addPageIsDisabled, onLoadMoreAnchor, loadingTemplateId } = data;

  const templateData = templates[index];
  if (!templateData) {
    return null;
  }

  const { template_id: templateId, title, type: permission, isMultipage, categories } = templateData;
  const userHasPermission = hasTemplatePermission(userType, permission);
  const isAccessibleTemplate = categories.includes(E2_TEMPLATE_CATEGORY);

  // On click, add template or redirect to account page if user does not have permisison
  const handleClick = () => {
    onSelectTemplate(templateData);
  };

  const url = `${TEMPLATE_THUMBNAIL_URL}${templateId}.webp?retryCount=0`;
  const isLoading = isMultipage === 0 && loadingTemplateId === templateId;

  const handleMouseEnter = () => {
    setIsHovered.on();

    // Blur the active element to prevent multiple hover states
    blurTemplateOverlayButtonIfActive();
  };

  const handleFocus = (e: FocusEvent) => {
    e.stopPropagation();
    setIsHovered.on();
  };

  const isAnchorCell =
    templates.length - INFINITE_SCROLL_INDEX_OFFSET >= 0
      ? index === templates.length - INFINITE_SCROLL_INDEX_OFFSET
      : index === templates.length - 1;

  return (
    <Box
      key={`${templateId}-thumbnail-${isAnchorCell}`}
      h='full'
      w='full'
      position='relative'
      onMouseEnter={handleMouseEnter}
      onMouseLeave={setIsHovered.off}
      onFocus={handleFocus}
      onBlur={setIsHovered.off}
      _focus={{ outline: 'none' }}
      borderRadius='base'
      style={style}
      pt={`${PADDING_SIZE}px`}
      pl={columnIndex !== 0 ? `${PADDING_SIZE / 2}px` : 0}
      pr={columnIndex === 0 ? `${PADDING_SIZE / 2}px` : 0}
      data-testid={`${templateId}-thumbnail`}
      id={`${templateId}-thumbnail`}
      ref={isAnchorCell ? onLoadMoreAnchor : null}
    >
      <Box
        h='full'
        w='full'
        position='relative'
        p={0}
        borderRadius='base'
        border='1px solid'
        borderColor='gray.200'
        role='group'
      >
        <TemplateImage templateId={templateId} title={title} url={url} />
        <ThumbnailOverlay
          onClick={handleClick}
          show={isHovered}
          userHasPermission={userHasPermission}
          addPageIsDisabled={addPageIsDisabled}
          isLoading={isLoading}
          isMultipage={isMultipage === 1}
        />
        {permission !== TemplateType.Free && (
          <PermissionBadge permission={permission} position='absolute' top={-2} right={2} />
        )}
        {isAccessibleTemplate && <AccessibleTemplateTag />}
      </Box>
    </Box>
  );
}, areEqual);

interface TemplateImageProps {
  url: string;
  title: string;
  templateId: string;
}

const TemplateImage = memo(({ url, title, templateId }: TemplateImageProps) => (
  <Image
    key={`${templateId}-thumbnail`}
    w='full'
    h='full'
    src={url}
    objectFit='contain'
    objectPosition={'center'}
    borderRadius='base'
    alt={`Add page from template ${title}`}
    loading='lazy'
  />
));
TemplateImage.displayName = 'TemplateImage';

TemplateThumbnail.displayName = 'TemplateThumbnail';
